import { fetchToken } from '../helpers/fetch';
import { const_routes } from '../helpers/const_routes';
import { types } from '../types/types';
import { startLogout } from './authActions';
import { startLoadingConfigurations } from './configurationActions';
import Swal from 'sweetalert2';
import { t } from 'i18next';

const handleAuthErrors = (dispatch, body) => {
  if (!body || body.ok !== false) return false;
  if (body.code === 'bak-aut-tok-inv-001') {
    Swal.fire(t('titles.error'), t('alerts.validation.bak-aut-tok-inv-001'), 'error');
    dispatch(startLogout());
    return true;
  }
  if (body.code === 'bak-aut-tok-inv-002' || body.code === 'bak-aut-tok-001' || body.code === 'bak-aut-tok-002') {
    Swal.fire(t('titles.error'), t('api.token.timed_out'), 'error');
    dispatch(startLogout());
    return true;
  }
  if (body.code === 'bak-error-001') {
    Swal.fire(
      t('titles.error'),
      `${t('alerts.validation.bak-error-001')} - ${body.method || ''}`,
      'error'
    );
    return true;
  }
  return false;
};

const call = async (endpoint, data, method = 'GET') => {
  const resp = await fetchToken(
    `${const_routes.SCHEMA}`,
    `${const_routes.VERSIONV1}`,
    `${const_routes.ROUTE_SUPPORT_FORM}`,
    endpoint,
    data,
    method
  );
  return resp.json();
};

export const startLoadingSupportForms = () => {
  return async (dispatch) => {
    dispatch({ type: types.SUPPORT_FORM_SET_LOADING, payload: true });
    try {
      const body = await call('get');
      if (body.ok) {
        dispatch({ type: types.SUPPORT_FORM_SET_LIST, payload: body.forms || [] });
      } else {
        dispatch({ type: types.SUPPORT_FORM_SET_LOADING, payload: false });
        if (!handleAuthErrors(dispatch, body) && body.msg) {
          Swal.fire(t('titles.error'), body.msg, 'error');
        }
      }
      return body;
    } catch (error) {
      dispatch({ type: types.SUPPORT_FORM_SET_LOADING, payload: false });
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false, msg: String(error) };
    }
  };
};

export const startLoadSupportFormDetail = (id) => {
  return async (dispatch) => {
    try {
      const body = await call(`get/detail/${id}`);
      if (body.ok) {
        dispatch({ type: types.SUPPORT_FORM_SET_SELECTED, payload: body.form || null });
      } else {
        handleAuthErrors(dispatch, body);
        if (body.msg) Swal.fire(t('titles.alert'), body.msg, 'warning');
      }
      return body;
    } catch (error) {
      return { ok: false, msg: String(error) };
    }
  };
};

export const startReplaceSupportFormQuestions = (formId, questions) => {
  return async (dispatch) => {
    try {
      const body = await call(`questions/replace/${formId}`, { questions }, 'PUT');
      if (body.ok) {
        dispatch({ type: types.SUPPORT_FORM_SET_SELECTED, payload: body.form || null });
        await dispatch(startLoadingSupportForms());
        Swal.fire(t('titles.success'), t('semanticSearch.formAdmin.saved', { defaultValue: 'Preguntas guardadas.' }), 'success');
      } else if (!handleAuthErrors(dispatch, body)) {
        Swal.fire(t('titles.alert'), body.msg || t('alerts.validation.bak-error-001'), 'warning');
      }
      return body;
    } catch (error) {
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false, msg: String(error) };
    }
  };
};

export const startToggleSupportFormEnabled = (form) => {
  return async (dispatch) => {
    try {
      const body = await call(`update/${form.id}`, { enabled: !form.enabled }, 'PUT');
      if (body.ok) {
        await dispatch(startLoadingSupportForms());
      } else if (!handleAuthErrors(dispatch, body) && body.msg) {
        Swal.fire(t('titles.alert'), body.msg, 'warning');
      }
      return body;
    } catch (error) {
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false };
    }
  };
};

export const startSeedSupportFormCatalog = () => {
  return async (dispatch) => {
    try {
      const body = await call('seed-catalog', {}, 'POST');
      if (body.ok) {
        dispatch({ type: types.SUPPORT_FORM_SET_LIST, payload: body.forms || [] });
        Swal.fire(
          t('titles.success'),
          t('semanticSearch.formAdmin.seeded', { defaultValue: 'Se copió el catálogo híbrido a las tablas.' }),
          'success'
        );
      } else if (!handleAuthErrors(dispatch, body)) {
        Swal.fire(t('titles.alert'), body.msg || t('alerts.validation.bak-error-001'), 'warning');
      }
      return body;
    } catch (error) {
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false };
    }
  };
};

export const startSetSupportFormMode = (mode) => {
  return async (dispatch) => {
    try {
      const listResp = await fetchToken(
        `${const_routes.SCHEMA}`,
        `${const_routes.VERSIONV1}`,
        `${const_routes.ROUTE_CONFIGURATION}`,
        'get'
      );
      const listBody = await listResp.json();
      if (!listBody.ok) {
        handleAuthErrors(dispatch, listBody);
        return listBody;
      }
      const rows = listBody.configurations || listBody.configuration || [];
      const row = (Array.isArray(rows) ? rows : []).find((c) => c.id_field === 'support.form.mode');
      if (!row || !row.id) {
        Swal.fire(
          t('titles.alert'),
          t('semanticSearch.formAdmin.modeMissing', { defaultValue: 'No existe la configuración support.form.mode.' }),
          'warning'
        );
        return { ok: false };
      }
      const resp = await fetchToken(
        `${const_routes.SCHEMA}`,
        `${const_routes.VERSIONV1}`,
        `${const_routes.ROUTE_CONFIGURATION}`,
        `update/${row.id}`,
        { ...row, value: mode },
        'PUT'
      );
      const body = await resp.json();
      if (body.ok) {
        await dispatch(startLoadingConfigurations());
        Swal.fire(
          t('titles.success'),
          t('semanticSearch.formAdmin.modeSaved', { defaultValue: 'Modo del formulario actualizado. Recargue Búsqueda para ver el popup.' }),
          'success'
        );
      } else if (!handleAuthErrors(dispatch, body) && body.msg) {
        Swal.fire(t('titles.alert'), body.msg, 'warning');
      }
      return body;
    } catch (error) {
      Swal.fire(t('titles.error'), String(error), 'error');
      return { ok: false };
    }
  };
};
